import React from "react";
import {
  Stack,
  Button,
  Checkbox,
  TextField,
  createFilterOptions,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import InputControl from "../../../shared/components/InputControl";
import { Loader, ServerPaginatedTable, useDropdownApis } from "../../../shared";
import { DashBoardDateRangePicker } from "../../Dashboard";
import { arraySort, sortItems } from "../../../utils";
import { useBirthdayCouponUsageHistoryV2 } from "../hooks";

export const BirthdayCouponUsageHistoryV2 = ({ couponId }) => {
  const {
    state,
    onChangeUsageHistoryFilter,
    onChangeDate,
    clearSearchUsageHistoryFilter,
    handleDateChangeApply,
    handleIsOpen,
    handleStoreFilter,
    handleApplyFilter,
    handleFilterClear,
    resetUsageHistoryFilters,
    gotoPageUsageHistory,
    setPageSizeUsageHistory,
    handleSortUsageHistory,
    handleDownloadUsageHistory,
  } = useBirthdayCouponUsageHistoryV2({ couponId });

  const { storeList } = useDropdownApis({ isStoreList: true });

  const storeOptions = storeList.map((store) => {
    return { value: store.id, label: store.name };
  });

  const filterOptions = createFilterOptions({
    matchFrom: "any",
    stringify: (option) => option.label,
  });

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isStoreAdmin = pathname.includes("store-admin");

  const columns = React.useMemo(
    () => [
      {
        Header: "Order Number",
        accessor: "order_number",
        Cell: ({ row }) => {
          return (
            <Stack direction="row" gap="12px" alignItems="center">
              <div
                className="table-taxt-and-icon-link cursor"
                onClick={() =>
                  navigate(
                    isStoreAdmin
                      ? `/store-admin/orders/details/${row.original.order_id}`
                      : `/orders/details/${row.original.order_id}`,
                  )
                }
              >
                <span className="link-text">{row.original.order_number}</span>
              </div>
            </Stack>
          );
        },
        sortType: (prev, curr, columnId) => {
          return sortItems(prev, curr, columnId);
        },
      },
      {
        Header: "Customer Name",
        accessor: "customer_name",
        Cell: ({ row }) => {
          return (
            <Stack direction="row" gap="12px" alignItems="center">
              {row.original.customer_id === null ? (
                row.original.customer_name
              ) : (
                <div
                  className="table-taxt-and-icon-link cursor"
                  onClick={() =>
                    navigate(
                      isStoreAdmin
                        ? `/store-admin/customers/details/${row.original.customer_id}`
                        : `/customers/details/${row.original.customer_id}`,
                    )
                  }
                >
                  <span className="link-text">
                    {row.original.customer_name}
                  </span>
                </div>
              )}
            </Stack>
          );
        },
        sortType: (prev, curr, columnId) => {
          return sortItems(prev, curr, columnId);
        },
      },
      {
        Header: "Used Date & Time",
        accessor: "order_date",
      },
      {
        Header: "Store",
        accessor: "store_name",
      },
    ],
    [isStoreAdmin, navigate],
  );

  if (state.isLoading) {
    return <Loader />;
  }

  return (
    <ServerPaginatedTable
      rowsSelection={false}
      columns={columns}
      data={state.usageHistory.data}
      isLoading={state.isLoading}
      filterApplied={state.usageHistoryFilters.all}
      onFilterClear={(e) => handleFilterClear("single", e)}
      onAllFilterClear={(e) => handleFilterClear("all")}
      gotoPage={gotoPageUsageHistory}
      setPageSize={setPageSizeUsageHistory}
      pagination={state.usageHistory.pagination}
      sortByGlobal={state.usageHistory.sortBy}
      handleSort={handleSortUsageHistory}
      defaultSearch={false}
      searchBar={true}
      filterContent={
        <>
          <InputControl
            type="table-search"
            name="searchText"
            placeholder="Search"
            size="small"
            value={state.usageHistoryFilters.searchText}
            onChange={(e) => onChangeUsageHistoryFilter(e)}
            onClear={() => clearSearchUsageHistoryFilter()}
          />
          <Button
            type="button"
            size="medium"
            variant="text"
            onClick={handleDownloadUsageHistory}
          >
            <img
              alt="export"
              src="/icons/ic_export.svg"
              style={{
                marginRight: "4px",
              }}
            />
            Export
          </Button>
        </>
      }
      filterSection={
        <>
          <Stack direction="row" gap="16px" alignItems="center" flexWrap="wrap">
            <DashBoardDateRangePicker
              onChangeDate={onChangeDate}
              isList={true}
              range={state.date}
              showRange={state.showDate}
              isOpen={state.isCalenderOpen}
              handleIsOpen={handleIsOpen}
              handleDateChangeApply={handleDateChangeApply}
              position="left"
            />

            <InputControl
              type="dropdown"
              name="Store"
              label="Store"
              multiple={true}
              options={arraySort(storeOptions || [], "label")}
              value={state.usageHistoryFilters.stores}
              disableCloseOnSelect
              disableClearable={true}
              onChange={handleStoreFilter}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) =>
                option.value === value.value
              }
              renderTags={(value) => value.map(() => <></>)}
              renderOption={(props, option, { selected }) => (
                <>
                  <li {...props} key={option.value}>
                    <Checkbox style={{ marginRight: 8 }} checked={selected} />
                    {option.label}
                  </li>
                </>
              )}
              filterOptions={filterOptions}
              sx={{ width: 250 }}
              componentsProps={{
                paper: {
                  sx: {
                    width: "fit-content",
                    minWidth: 250,
                  },
                },
              }}
              renderInput={(params) => (
                <TextField {...params} label="Store" placeholder="Store" />
              )}
            />
          </Stack>

          <Button
            type="button"
            size="medium"
            variant="text"
            color="info"
            sx={{
              minWidth: "40px",
              p: "8px",
            }}
            onClick={handleApplyFilter}
            disabled={state.disableApplyButton}
          >
            Apply
          </Button>
        </>
      }
    />
  );
};
